import { QueryClient, useQueryClient } from '@tanstack/react-query'

import {
  getBusinessClientsQueryKey,
  useBusinessClientsQuery,
} from '@/hooks/business/useBusinessClientsQuery'
import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'
import {
  getBusinessUsersQueryKey,
  useBusinessUsersQuery,
} from '@/hooks/business/useBusinessUsersQuery'
import { DeliveryPerson } from '@/hooks/business/useDeliveryPersonnelQuery'
import {
  EconomicCycle,
  getEconomicCyclesQueryKey,
  useEconomicCyclesQuery,
} from '@/hooks/business/useEconomicCyclesQuery'
import {
  getProductsQueryKey,
  Product as ProductInterface,
  useProductsQuery,
} from '@/hooks/business/useProductsQuery'
import {
  ClientInterface,
  PaymentGatewayInterface,
  UserInterface,
} from '@/interfaces/ServerInterfaces'

// Types for filter options
export interface FilterOption {
  value: string
  label: string
}

export interface Commercial {
  id: number
  username: string
  displayName: string
  email: string
  isActive: boolean
  businessId: number
  avatar: string | null
  lastLogin: string
  roles: {
    name: string
    code: string
  }[]
}

export interface Product {
  id: number
  name: string
  salesCode: string
  type: string
}

export interface AreaSales {
  id: number
  name: string
}

export interface PriceSystem {
  id: number
  name: string
  isMain: boolean
}

/**
 * Client Cache Helper - Provides centralized client caching functionality
 * This helps reduce duplicate queries and improves performance across the app
 */
export class ClientCacheHelper {
  private queryClient: QueryClient

  constructor(queryClient: QueryClient) {
    this.queryClient = queryClient
  }

  /**
   * Cache a client across multiple query keys for maximum hit rate
   */
  cacheClient(client: ClientInterface | null, businessId: number) {
    if (!client) return
    // Cache for direct ID lookup
    this.queryClient.setQueryData(
      getBusinessClientsQueryKey({
        businessId,
        customerId: client.id,
      }),
      {
        items: [client],
        totalItems: 1,
        currentPage: 1,
        totalPages: 1,
      }
    )
  }

  /**
   * Check if client data exists in cache
   */
  hasClientInCache(clientId: number, businessId: number): boolean {
    const data = this.queryClient.getQueryData(
      getBusinessClientsQueryKey({
        businessId,
        customerId: clientId,
      })
    )
    return !!data
  }

  /**
   * Get client from cache if available
   */
  getClientFromCache(
    clientId: number,
    businessId: number
  ): ClientInterface | null {
    const data = this.queryClient.getQueryData(
      getBusinessClientsQueryKey({
        businessId,
        customerId: clientId,
      })
    ) as { items: ClientInterface[] } | undefined

    return data?.items?.[0] || null
  }
}

/**
 * Hook to get the client cache helper instance
 */
export const useClientCache = () => {
  const queryClient = useQueryClient()
  return new ClientCacheHelper(queryClient)
}

/**
 * User Cache Helper - Provides centralized user caching functionality
 * This helps reduce duplicate queries and improves performance across the app
 */
export class UserCacheHelper {
  private queryClient: QueryClient

  constructor(queryClient: QueryClient) {
    this.queryClient = queryClient
  }

  /**
   * Cache a user across multiple query keys for maximum hit rate
   */
  cacheUser(user: UserInterface, businessId: number) {
    // Cache for direct ID lookup
    this.queryClient.setQueryData(
      getBusinessUsersQueryKey({
        businessId,
        userId: user.id,
      }),
      {
        items: [user],
        totalItems: 1,
        currentPage: 1,
        totalPages: 1,
      }
    )
  }

  /**
   * Check if user data exists in cache
   */
  hasUserInCache(userId: number, businessId: number): boolean {
    const data = this.queryClient.getQueryData(
      getBusinessUsersQueryKey({
        businessId,
        userId,
      })
    )
    return !!data
  }

  /**
   * Get user from cache if available
   */
  getUserFromCache(userId: number, businessId: number): UserInterface | null {
    const data = this.queryClient.getQueryData(
      getBusinessUsersQueryKey({
        businessId,
        userId,
      })
    ) as { items: UserInterface[] } | undefined

    return data?.items?.[0] || null
  }
}

/**
 * Product Cache Helper - Provides centralized product caching functionality
 * This helps reduce duplicate queries and improves performance across the app
 */
export class ProductCacheHelper {
  private queryClient: QueryClient

  constructor(queryClient: QueryClient) {
    this.queryClient = queryClient
  }

  /**
   * Cache a product across multiple query keys for maximum hit rate
   */
  cacheProduct(product: ProductInterface, businessId: number) {
    // Cache for direct ID lookup
    this.queryClient.setQueryData(
      getProductsQueryKey({
        businessId,
        productId: product.id,
      }),
      {
        items: [product],
        totalItems: 1,
        currentPage: 1,
        totalPages: 1,
      }
    )
  }

  /**
   * Check if product data exists in cache
   */
  hasProductInCache(productId: number, businessId: number): boolean {
    const data = this.queryClient.getQueryData(
      getProductsQueryKey({
        businessId,
        productId,
      })
    )
    return !!data
  }

  /**
   * Get product from cache if available
   */
  getProductFromCache(
    productId: number,
    businessId: number
  ): ProductInterface | null {
    const data = this.queryClient.getQueryData(
      getProductsQueryKey({
        businessId,
        productId,
      })
    ) as { items: ProductInterface[] } | undefined

    return data?.items?.[0] || null
  }
}

/**
 * Economic Cycle Cache Helper - Provides centralized economic cycle caching functionality
 * This helps reduce duplicate queries and improves performance across the app
 */
export class EconomicCycleCacheHelper {
  private queryClient: QueryClient

  constructor(queryClient: QueryClient) {
    this.queryClient = queryClient
  }

  /**
   * Cache an economic cycle across multiple query keys for maximum hit rate
   */
  cacheEconomicCycle(economicCycle: EconomicCycle, businessId: number) {
    // Cache for direct ID lookup
    this.queryClient.setQueryData(
      getEconomicCyclesQueryKey({
        businessId,
        economicCycleId: economicCycle.id,
      }),
      {
        items: [economicCycle],
        totalItems: 1,
        currentPage: 1,
        totalPages: 1,
      }
    )
  }

  /**
   * Check if economic cycle data exists in cache
   */
  hasEconomicCycleInCache(cycleId: number, businessId: number): boolean {
    const data = this.queryClient.getQueryData(
      getEconomicCyclesQueryKey({
        businessId,
        economicCycleId: cycleId,
      })
    )
    return !!data
  }

  /**
   * Get economic cycle from cache if available
   */
  getEconomicCycleFromCache(
    cycleId: number,
    businessId: number
  ): EconomicCycle | null {
    const data = this.queryClient.getQueryData(
      getEconomicCyclesQueryKey({
        businessId,
        economicCycleId: cycleId,
      })
    ) as { items: EconomicCycle[] } | undefined

    return data?.items?.[0] || null
  }
}

/**
 * Hook to get the user cache helper instance
 */
export const useUserCache = () => {
  const queryClient = useQueryClient()
  return new UserCacheHelper(queryClient)
}

/**
 * Hook to get the product cache helper instance
 */
export const useProductCache = () => {
  const queryClient = useQueryClient()
  return new ProductCacheHelper(queryClient)
}

/**
 * Hook to get the economic cycle cache helper instance
 */
export const useEconomicCycleCache = () => {
  const queryClient = useQueryClient()
  return new EconomicCycleCacheHelper(queryClient)
}

// Utility function to get client display name with proper fallback hierarchy
export const getClientDisplayName = (client: ClientInterface): string => {
  // 1. Try name + lastName combination
  if (client?.name && client?.lastName) {
    const result = `${client.name.trim()} ${client.lastName.trim()}`.trim()
    return result
  }

  // 2. Try just name
  if (client?.name && client.name.trim()) {
    const result = client.name.trim()
    return result
  }

  // 3. Try firstName + lastName (legacy fields)
  if (client?.firstName && client?.lastName) {
    const result = `${client.firstName.trim()} ${client.lastName.trim()}`.trim()
    return result
  }

  // 4. Try just firstName (legacy field)
  if (client?.firstName && client.firstName.trim()) {
    const result = client.firstName.trim()
    return result
  }

  // 5. Try email
  if (client?.email && client.email.trim()) {
    const result = client.email.trim()
    return result
  }

  // 6. Try first available phone number
  if (client?.phones && client.phones.length > 0) {
    const firstPhone = client.phones.find(
      (phone) => phone.number && phone.number.trim()
    )
    if (firstPhone) {
      const result = firstPhone.number.trim()
      return result
    }
  }

  // 7. Last resort: use ID
  const result = `Cliente: ${client?.id ?? ''}`
  return result
}

// Utility function to get user display name with proper fallback hierarchy
export const getUserDisplayName = (user: UserInterface): string => {
  // 1. Try displayName (preferred)
  if (user?.displayName && user.displayName.trim()) {
    return user.displayName.trim()
  }

  // 2. Try username
  if (user?.username && user.username.trim()) {
    return user.username.trim()
  }

  // 3. Try email
  if (user?.email && user.email.trim()) {
    return user.email.trim()
  }

  // 4. Last resort: use ID
  return `Usuario: ${user?.id ?? ''}`
}

// Utility function to get product display name with proper fallback hierarchy
export const getProductDisplayName = (product: ProductInterface): string => {
  // 1. Try name (primary)
  if (product?.name && product.name.trim()) {
    return product.name.trim()
  }

  // 2. Try salesCode as fallback
  if (product?.salesCode && product.salesCode.trim()) {
    return `${product.salesCode.trim()} (por código)`
  }

  // 3. Last resort: use ID
  return `Producto: ${product?.id ?? ''}`
}

// Utility function to get economic cycle display name with proper fallback hierarchy
export const getEconomicCycleDisplayName = (cycle: EconomicCycle): string => {
  // 1. Try name (primary)
  if (cycle?.name && cycle.name.trim()) {
    const name = cycle.name.trim()
    const status = cycle.isActive ? ' (Activo)' : ''
    return `${name}${status}`
  }

  // 2. Generate name from creator info
  const creatorName =
    cycle?.openBy?.displayName ??
    cycle?.openBy?.username ??
    cycle?.openBy?.email ??
    'Usuario desconocido'

  const status = cycle.isActive ? ' (Activo)' : ''

  // 3. Last resort: use creator + ID
  return `Creado por ${creatorName} - ${cycle?.id ?? ''}${status}`
}

/**
 * Centralized hook for fetching and resolving client data by ID
 * Provides consistent client labels across all components
 */
export const useClientById = (clientId: string | null | undefined) => {
  const { businessData, isLoadingBusinessData } = useBusinessDataQuery({})

  // Fetch specific client data when clientId exists
  const { businessClients, isLoadingBusinessClients, errorBusinessClients } =
    useBusinessClientsQuery({
      businessId: businessData?.id,
      params: {
        customerId: clientId ? parseInt(clientId) : undefined,
      },
      options: {
        enabled: !!clientId && !!businessData?.id && !isLoadingBusinessData,
        // Longer cache times for client data (clients don't change frequently)
        staleTime: 10 * 60 * 1000, // 10 minutes
        gcTime: 30 * 60 * 1000, // 30 minutes (keep in cache longer)

        // Network optimizations
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,

        // Retry configuration for network resilience
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),

        // Keep previous data while refetching for better UX
        placeholderData: (previousData) => previousData,
      },
    })

  if (clientId === null) {
    return {
      client: null,
      clientLabel: null,
      isLoading: false,
      error: null,
      hasData: false,
    }
  }

  const client =
    businessClients && businessClients.length > 0 ? businessClients[0] : null

  // Generate consistent label
  const clientLabel = clientId
    ? client
      ? getClientDisplayName(client)
      : `Cliente: ${clientId}`
    : null

  const result = {
    client,
    clientLabel,
    isLoading: isLoadingBusinessClients,
    error: errorBusinessClients,
    hasData: !!client,
  }

  return result
}

/**
 * Centralized hook for fetching and resolving user data by ID
 * Provides consistent user labels across all components
 */
export const useUserById = (userId: string | null | undefined) => {
  const { businessData, isLoadingBusinessData } = useBusinessDataQuery({})

  // Fetch specific user data when userId exists
  const { businessUsers, isLoadingBusinessUsers, errorBusinessUsers } =
    useBusinessUsersQuery({
      businessId: businessData?.id ?? 0,
      params: {
        userId: userId ? parseInt(userId) : undefined,
      },
      options: {
        enabled: !!userId && !!businessData?.id && !isLoadingBusinessData,
        // Longer cache times for user data (users don't change frequently)
        staleTime: 10 * 60 * 1000, // 10 minutes
        gcTime: 30 * 60 * 1000, // 30 minutes (keep in cache longer)

        // Network optimizations
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,

        // Retry configuration for network resilience
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),

        // Keep previous data while refetching for better UX
        placeholderData: (previousData) => previousData,
      },
    })

  const user =
    businessUsers && businessUsers.length > 0 ? businessUsers[0] : null

  // Generate consistent label
  const userLabel = userId
    ? user
      ? getUserDisplayName(user)
      : `Usuario: ${userId}`
    : null

  return {
    user,
    userLabel,
    isLoading: isLoadingBusinessUsers,
    error: errorBusinessUsers,
    hasData: !!user,
  }
}

/**
 * Centralized hook for fetching and resolving product data by ID
 * Provides consistent product labels across all components
 */
export const useProductById = (productId: string | null | undefined) => {
  const { businessData, isLoadingBusinessData } = useBusinessDataQuery({})

  // Fetch specific product data when productId exists
  const { products, isLoadingProducts, errorProducts } = useProductsQuery({
    businessId: businessData?.id ?? 0,
    params: {
      productId: productId ? parseInt(productId) : undefined,
    },
    options: {
      enabled: !!productId && !!businessData?.id && !isLoadingBusinessData,
      // Longer cache times for product data (products change less frequently)
      staleTime: 15 * 60 * 1000, // 15 minutes
      gcTime: 45 * 60 * 1000, // 45 minutes (keep in cache longer)

      // Network optimizations
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,

      // Retry configuration for network resilience
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),

      // Keep previous data while refetching for better UX
      placeholderData: (previousData) => previousData,
    },
  })

  const product = products && products.length > 0 ? products[0] : null

  // Generate consistent label
  const productLabel = productId
    ? product
      ? getProductDisplayName(product)
      : `Producto: ${productId}`
    : null

  return {
    product,
    productLabel,
    isLoading: isLoadingProducts,
    error: errorProducts,
    hasData: !!product,
  }
}

/**
 * Centralized hook for fetching and resolving economic cycle data by ID
 * Provides consistent economic cycle labels across all components
 */
export const useEconomicCycleById = (cycleId: string | null | undefined) => {
  const { businessData, isLoadingBusinessData } = useBusinessDataQuery({})

  // Fetch specific economic cycle data when cycleId exists
  const { economicCycles, isLoadingEconomicCycles, errorEconomicCycles } =
    useEconomicCyclesQuery({
      businessId: businessData?.id ?? 0,
      params: {
        economicCycleId: cycleId ? parseInt(cycleId) : undefined,
      },
      options: {
        enabled: !!cycleId && !!businessData?.id && !isLoadingBusinessData,
        // Longer cache times for economic cycle data
        staleTime: 15 * 60 * 1000, // 15 minutes
        gcTime: 45 * 60 * 1000, // 45 minutes (keep in cache longer)

        // Network optimizations
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,

        // Retry configuration for network resilience
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),

        // Keep previous data while refetching for better UX
        placeholderData: (previousData) => previousData,
      },
    })

  const economicCycle =
    economicCycles && economicCycles.length > 0
      ? economicCycles.find((cycle) => cycle.id === parseInt(cycleId ?? '0'))
      : null

  // Generate consistent label
  const economicCycleLabel = cycleId
    ? economicCycle
      ? getEconomicCycleDisplayName(economicCycle)
      : `Ciclo: ${cycleId}`
    : null

  return {
    economicCycle,
    economicCycleLabel,
    isLoading: isLoadingEconomicCycles,
    error: errorEconomicCycles,
    hasData: !!economicCycle,
  }
}

// Utility functions to convert data to filter options

export const clientsToOptions = (
  clients: ClientInterface[]
): FilterOption[] => {
  return clients.map((client) => ({
    value: client.id.toString(),
    label: getClientDisplayName(client),
  }))
}

export const usersToOptions = (users: UserInterface[]): FilterOption[] => {
  return users.map((user) => ({
    value: user.id.toString(),
    label: user.displayName || user.username || user.email,
  }))
}

export const commercialsToOptions = (
  commercials: Commercial[]
): FilterOption[] => {
  return commercials.map((commercial) => ({
    value: commercial.id.toString(),
    label: commercial.displayName,
  }))
}

export const productsToOptions = (products: Product[]): FilterOption[] => {
  return products.map((product) => ({
    value: product.id.toString(),
    label: product.name,
  }))
}

export const areaSalesToOptions = (areaSales: AreaSales[]): FilterOption[] => {
  return areaSales.map((area) => ({
    value: area.id.toString(),
    label: area.name,
  }))
}

export const deliveryPersonnelToOptions = (
  users: DeliveryPerson[]
): FilterOption[] => {
  return users.map((user) => ({
    value: user.id.toString(),
    label: user.displayName,
  }))
}

export const paymentGatewaysToOptions = (
  paymentGateways: PaymentGatewayInterface[]
): FilterOption[] => {
  return paymentGateways.map((paymentGateway) => ({
    value: paymentGateway.id.toString(),
    label: paymentGateway.name,
  }))
}

export const economicCyclesToOptions = (
  cycles: EconomicCycle[]
): FilterOption[] => {
  return cycles.map((cycle) => ({
    value: cycle.id.toString(),
    label: `${
      cycle.name ||
      `Creado por ${
        cycle.openBy.displayName ?? cycle.openBy.username ?? cycle.openBy.email
      }-${cycle.id}`
    }${cycle.isActive ? ' (Activo)' : ''}`,
  }))
}

export const priceSystemsToOptions = (
  systems: PriceSystem[]
): FilterOption[] => {
  return systems.map((system) => ({
    value: system.id.toString(),
    label: system.name,
  }))
}
